/**
 * Completeness Page
 *
 * Encourages a full and complete expression of each item — every available
 * field and attribute is surfaced as an opportunity to enrich.
 *
 * This page is NOT a gate check. Readiness gates live on the Readiness page.
 * Here, more complete = better, and every field filled is a small win.
 */

import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquare,
  CheckCircle2,
  Circle,
  Search,
  ExternalLink,
  ArrowRight,
  X,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '@/lib/itemCategoryColors';
import type { Candidate } from '@/types/prioritization/lifecycle';
import { itemHasValue } from '@/lib/itemReadiness';
import { useDefinitionPolicyStore } from '@/stores/prioritization/definitionPolicyStore';
import { useValueProposalStore } from '@/stores/prioritization/valueProposalStore';
import type { DefinitionBehavior } from '@/types/prioritization/definitionPolicy';

// ── Section definitions ───────────────────────────────────────────────────────

type SectionKey = 'identity' | 'context' | 'ownership' | 'relationships' | 'attributes';

const SECTION_ORDER: SectionKey[] = [
  'identity',
  'context',
  'ownership',
  'relationships',
  'attributes',
];

const SECTION_META: Record<
  SectionKey,
  {
    label: string;
    description: string;
    fillClass: string;
    emptyClass: string;
    iconClass: string;
  }
> = {
  identity: {
    label: 'Classification',
    description: 'Category, altitude, and portfolio alignment',
    fillClass: 'bg-[var(--slate)]',
    emptyClass: 'bg-[var(--slate-dim)]',
    iconClass: 'text-[var(--text-ter)]',
  },
  context: {
    label: 'Context',
    description: 'What this is, why it matters, who it serves',
    fillClass: 'bg-[var(--slate)]',
    emptyClass: 'bg-[var(--slate-dim)]',
    iconClass: 'text-[var(--text-ter)]',
  },
  ownership: {
    label: 'Ownership',
    description: 'Who is accountable for this item',
    fillClass: 'bg-[var(--green)]',
    emptyClass: 'bg-[var(--green-dim)]',
    iconClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  },
  relationships: {
    label: 'Relationships',
    description: 'Parent items and duplicate status',
    fillClass: 'bg-[var(--mustard)]',
    emptyClass: 'bg-[var(--mustard-dim)]',
    iconClass: 'text-[var(--mustard)]',
  },
  attributes: {
    label: 'Attributes',
    description: 'Custom scoring and context fields',
    fillClass: 'bg-[var(--green)]',
    emptyClass: 'bg-[var(--green-dim)]',
    iconClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  },
};

// ── Field definitions ─────────────────────────────────────────────────────────

interface FieldSpec {
  key: string;
  label: string;
  section: SectionKey;
  /** The workspace's every-item rule for this field (Settings → Readiness → Every item). */
  rule: DefinitionBehavior;
  check: (item: Candidate) => boolean;
}

/** Fields shown here whose every-item rule is stored under another key. */
const RULE_KEY: Record<string, string> = { desiredOutcome: 'rationale' };

const BASE_FIELDS: Array<Omit<FieldSpec, 'rule'>> = [
  // ── Classification ──────────────────────────────────────────────────
  {
    key: 'itemCategory',
    label: 'Category',
    section: 'identity',
    check: (i) => !!i.itemCategory,
  },
  {
    key: 'altitudeLevel',
    label: 'Altitude',
    section: 'identity',
    check: (i) => !!(i.altitudeLevel ?? (i as any).altitude),
  },
  {
    key: 'strategicBucket',
    label: 'Strategic bucket',
    section: 'identity',
    check: (i) => !!i.strategicBucket,
  },
  {
    key: 'metaCategory',
    label: 'Portfolio category',
    section: 'identity',
    check: (i) => !!i.metaCategory,
  },
  {
    key: 'work_type',
    label: 'Work type',
    section: 'identity',
    check: (i) => !!(i as any).work_type,
  },

  // ── Context & expression ────────────────────────────────────────────
  {
    key: 'description',
    label: 'Description',
    section: 'context',
    check: (i) => !!(i.description?.trim()),
  },
  {
    key: 'desiredOutcome',
    label: 'Desired outcome',
    section: 'context',
    check: (i) => !!(i.desiredOutcome?.trim()),
  },
  {
    key: 'beneficiary',
    label: 'Beneficiary',
    section: 'context',
    check: (i) => !!(i.beneficiary?.trim()),
  },
  {
    key: 'tags',
    label: 'Tags',
    section: 'context',
    check: (i) => !!(i.tags && i.tags.length > 0),
  },

  // ── Ownership ───────────────────────────────────────────────────────
  {
    key: 'ownerId',
    label: 'Owner',
    section: 'ownership',
    check: (i) => !!(i.ownerId ?? (i as any).shapingOwnerId),
  },
  {
    key: 'orgUnitId',
    label: 'Team / org unit',
    section: 'ownership',
    check: (i) => !!(i as any).orgUnitId,
  },

  // ── Relationships ───────────────────────────────────────────────────
  {
    key: 'parentItemId',
    label: 'Parent item',
    section: 'relationships',
    check: (i) => !!i.parentItemId,
  },
  {
    key: 'dedupeStatus',
    label: 'Duplicate check',
    section: 'relationships',
    check: (i) => !!(i as any).dedupeStatus,
  },
];

function buildFieldSpecs(
  attributes: Array<{ key: string; label: string; required: boolean }>,
  ruleFor: (fieldKey: string, attributeFallback?: { required: boolean }) => DefinitionBehavior,
): FieldSpec[] {
  const base: FieldSpec[] = BASE_FIELDS.map((f) => ({
    ...f,
    rule: ruleFor(RULE_KEY[f.key] ?? f.key),
  }));

  const attrFields: FieldSpec[] = attributes.map((a) => ({
    key: `attr:${a.key}`,
    label: a.label,
    section: 'attributes' as SectionKey,
    rule: ruleFor(`attr:${a.key}`, { required: a.required }),
    // Any capture path counts, a value accepted in a session included.
    check: (item) => itemHasValue(item, a.key),
  }));

  return [...base, ...attrFields];
}

// ── Scoring ───────────────────────────────────────────────────────────────────

interface FieldResult {
  key: string;
  label: string;
  section: SectionKey;
  rule: DefinitionBehavior;
  complete: boolean;
}

interface SectionScore {
  total: number;
  complete: number;
  fields: FieldResult[];
}

interface CompletenessScore {
  pct: number;
  complete: number;
  total: number;
  sections: Partial<Record<SectionKey, SectionScore>>;
}

function computeCompletenessScore(
  item: Candidate,
  fields: FieldSpec[]
): CompletenessScore {
  const sections: Partial<Record<SectionKey, SectionScore>> = {};

  for (const f of fields) {
    const complete = f.check(item);
    if (!sections[f.section]) {
      sections[f.section] = { total: 0, complete: 0, fields: [] };
    }
    const s = sections[f.section]!;
    s.total++;
    if (complete) s.complete++;
    s.fields.push({
      key: f.key,
      label: f.label,
      section: f.section,
      rule: f.rule,
      complete,
    });
  }

  const total = fields.length;
  const complete = fields.filter((f) => f.check(item)).length;
  return {
    pct: total === 0 ? 100 : Math.round((complete / total) * 100),
    complete,
    total,
    sections,
  };
}

// ── Segmented bar (table row) ─────────────────────────────────────────────────

function SegmentedBar({
  sections,
  total,
}: {
  sections: Partial<Record<SectionKey, SectionScore>>;
  total: number;
}) {
  if (total === 0) return <div className="h-2 w-full rounded-full bg-[var(--hover-tint)]" />;

  return (
    <div className="flex h-2 w-full gap-0.5">
      {SECTION_ORDER.map((key) => {
        const s = sections[key];
        if (!s || s.total === 0) return null;
        const meta = SECTION_META[key];
        const widthPct = (s.total / total) * 100;
        const fillPct = s.total === 0 ? 0 : (s.complete / s.total) * 100;

        return (
          <div
            key={key}
            className={`relative h-full overflow-hidden rounded-full ${meta.emptyClass}`}
            style={{ width: `${widthPct}%` }}
          >
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${meta.fillClass}`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Field entry (panel) ───────────────────────────────────────────────────────

function FieldEntry({
  field,
  sectionKey,
}: {
  field: FieldResult;
  sectionKey: SectionKey;
}) {
  const meta = SECTION_META[sectionKey];

  if (field.complete) {
    return (
      <div className="flex items-center gap-2.5 border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
        <CheckCircle2 className={`h-3.5 w-3.5 shrink-0 ${meta.iconClass}`} />
        <span className="flex-1 text-sm text-[var(--text-sec)]">{field.label}</span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)]">
          Filled
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5 border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2">
      <Circle className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)]" />
      <span className="flex-1 text-sm text-[var(--text-ter)]">{field.label}</span>
      {field.rule !== 'optional' && (
        <span
          className="rounded-full bg-[var(--hover-tint)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)]"
          title="Set in Settings → Readiness → Every item"
        >
          {field.rule === 'block' ? 'Required' : 'Recommended'}
        </span>
      )}
    </div>
  );
}

// ── Section group (panel) ─────────────────────────────────────────────────────

function SectionGroup({
  sectionKey,
  score,
}: {
  sectionKey: SectionKey;
  score: SectionScore;
}) {
  const meta = SECTION_META[sectionKey];
  const allDone = score.complete === score.total;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span
            className={`inline-block h-2 w-2 rounded-full ${meta.fillClass}`}
          />
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)]">
            {meta.label}
          </p>
        </div>
        <span
          className={`text-xs font-medium ${
            allDone ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'
          }`}
        >
          {score.complete} / {score.total}
        </span>
      </div>
      <div className="space-y-1.5">
        {score.fields.map((f) => (
          <FieldEntry key={f.key} field={f} sectionKey={sectionKey} />
        ))}
      </div>
    </div>
  );
}

// ── Circle percent (panel header) ─────────────────────────────────────────────

function CirclePercent({ pct }: { pct: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  // Encouraging palette — no red
  const color =
    pct === 100
      ? '#10b981'  // emerald
      : pct >= 75
      ? '#6366f1'  // indigo
      : pct >= 50
      ? '#8b5cf6'  // violet
      : '#14b8a6'; // teal

  return (
    <div className="relative flex shrink-0 items-center justify-center">
      <svg width="80" height="80" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="48"
          cy="48"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${filled} ${circ - filled}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-lg font-bold text-[var(--text-pri)]">{pct}%</span>
    </div>
  );
}

// ── Slide-out detail panel ────────────────────────────────────────────────────

function CompletenessPanel({
  item,
  score,
  onClose,
}: {
  item: Candidate;
  score: CompletenessScore;
  onClose: () => void;
}) {
  const encouragement =
    score.pct === 100
      ? 'This item is fully expressed — excellent work.'
      : score.pct >= 80
      ? 'Great shape. A few more fields will make this item even stronger.'
      : score.pct >= 50
      ? 'Good progress. More context helps the team evaluate fairly.'
      : 'There\'s room to enrich this item. Each field helps others understand it.';

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[var(--bg-raised)]">
      {/* Panel header */}
      <div className="flex-shrink-0 border-b border-[var(--rule)] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <CirclePercent pct={score.pct} />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-[var(--text-pri)] line-clamp-2">
                {item.title}
              </h2>
              <p className="mt-0.5 text-xs text-[var(--text-ter)]">
                {score.complete} of {score.total} fields filled
              </p>
              <p className="mt-1.5 text-xs leading-snug text-[var(--text-ter)]">
                {encouragement}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Section groups */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {score.total === 0 && (
          <div className="border border-dashed border-[var(--rule)] px-4 py-6 text-center">
            <p className="text-sm text-[var(--text-ter)]">No fields are configured yet.</p>
          </div>
        )}

        {SECTION_ORDER.map((key) => {
          const s = score.sections[key];
          if (!s || s.total === 0) return null;
          return <SectionGroup key={key} sectionKey={key} score={s} />;
        })}

        <div className="border-t border-[var(--rule)] pt-3">
          <Link
            to={`/prioritization/items/${item.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] hover:underline"
          >
            Open item to fill in fields <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ItemReadiness() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const rawAttributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'in-progress' | 'complete'>('all');

  // Accepted values count toward completeness, whatever path set them.
  const acceptedValues = useValueProposalStore((s) => s.acceptedValues);
  const valuesSynced = useValueProposalStore((s) => s.syncState.lastSyncAt != null || s.syncState.isLoading);
  const fetchValues = useValueProposalStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    fetchItems();
    fetchAttributes();
  }, [fetchItems, fetchAttributes, workspaceId]);

  useEffect(() => {
    if (!valuesSynced) void fetchValues();
  }, [valuesSynced, fetchValues]);

  const attributes = useMemo(
    () =>
      rawAttributes.map((a) => ({
        key: a.key,
        label: a.label ?? a.key,
        required: a.required ?? false,
      })),
    [rawAttributes]
  );

  const ruleOverrides = useDefinitionPolicyStore((s) => s.overrides);
  const ruleFor = useDefinitionPolicyStore((s) => s.getBehavior);
  const fieldSpecs = useMemo(
    () => {
      void ruleOverrides;
      void acceptedValues;
      return buildFieldSpecs(attributes, ruleFor);
    },
    [attributes, ruleFor, ruleOverrides, acceptedValues]
  );

  const activeItems = useMemo(
    () => items.filter((i) => (i as any).exit_status === 'active') as Candidate[],
    [items]
  );

  const scores = useMemo(() => {
    const m: Record<string, CompletenessScore> = {};
    for (const item of activeItems) {
      m[item.id] = computeCompletenessScore(item, fieldSpecs);
    }
    return m;
  }, [activeItems, fieldSpecs]);

  const displayItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered = q
      ? activeItems.filter(
          (i) =>
            i.title?.toLowerCase().includes(q) ||
            i.description?.toLowerCase().includes(q)
        )
      : activeItems;
    if (filter === 'in-progress') filtered = filtered.filter((i) => (scores[i.id]?.pct ?? 0) < 100);
    if (filter === 'complete') filtered = filtered.filter((i) => (scores[i.id]?.pct ?? 0) === 100);
    return [...filtered].sort(
      (a, b) => (scores[a.id]?.pct ?? 0) - (scores[b.id]?.pct ?? 0)
    );
  }, [activeItems, searchQuery, filter, scores]);

  const selectedItem = useMemo(
    () => activeItems.find((i) => i.id === selectedItemId) ?? null,
    [activeItems, selectedItemId]
  );

  const { completeCount, inProgressCount, avgPct } = useMemo(() => {
    const vals = activeItems.map((i) => scores[i.id]?.pct ?? 0);
    const complete = vals.filter((p) => p === 100).length;
    const avg =
      vals.length > 0
        ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length)
        : 0;
    return {
      completeCount: complete,
      inProgressCount: vals.length - complete,
      avgPct: avg,
    };
  }, [activeItems, scores]);

  // ── Zero state ─────────────────────────────────────────────────────────────

  if (activeItems.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-shrink-0 px-6 py-4">
          <PageHeader
            icon={<CheckSquare className="h-4 w-4 text-[var(--text-ter)]" />}
            iconBg="bg-[var(--slate-dim)]"
            title="Completeness"
            subtitle="See how fully each item is expressed and find opportunities to enrich it."
          />
        </div>
        <div className="flex flex-1 items-center justify-center p-8 text-center">
          <div>
            <Sparkles className="mx-auto mb-2 h-8 w-8 text-[var(--text-ter)]" />
            <p className="text-sm font-medium text-[var(--text-ter)]">No active items yet</p>
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              Items appear here once they are in your active pool.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Table + panel ──────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-4">
        <PageHeader
          icon={<CheckSquare className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Completeness"
          subtitle="How fully each active item is expressed. The more fields filled in, the better the team can evaluate it."
        />

        {/* Summary strip */}
        <div className="mt-3 flex flex-wrap items-center gap-5 border border-[var(--rule)] bg-[var(--surface)] px-4 py-2.5 text-sm">
          <span className="flex items-center gap-1.5 text-[var(--text-sec)]">
            <span className="font-semibold text-[var(--text-pri)]">{avgPct}%</span>
            <span className="text-[var(--text-ter)]">avg. completeness</span>
          </span>
          <span className="flex items-center gap-1.5 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="font-semibold">{completeCount}</span>
            <span>fully complete</span>
          </span>
          <span className="flex items-center gap-1.5 text-[var(--text-ter)]">
            <Circle className="h-3.5 w-3.5" />
            <span className="font-semibold">{inProgressCount}</span>
            <span>in progress</span>
          </span>

          {/* Section color key */}
          <div className="ml-auto flex items-center gap-3 text-xs text-[var(--text-ter)]">
            {SECTION_ORDER.map((key) => (
              <span key={key} className="flex items-center gap-1">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${SECTION_META[key].fillClass}`}
                />
                {SECTION_META[key].label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-3 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-2.5">
        <div className="flex items-center gap-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-1.5 w-64">
          <Search className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        <div className="flex gap-1">
          {(
            [
              ['all', 'All'],
              ['in-progress', 'In Progress'],
              ['complete', 'Complete'],
            ] as const
          ).map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              onClick={() => setFilter(val)}
              className={[
                ' px-3 py-1.5 text-xs font-medium transition-colors',
                filter === val
                  ? 'bg-[var(--slate)] text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]',
              ].join(' ')}
            >
              {lbl}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-[var(--text-ter)]">
          {displayItems.length} item{displayItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Body: table + right panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Table */}
        <div className="flex-1 min-w-0 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10 border-b border-[var(--rule)] bg-[var(--surface)]">
              <tr>
                <th className="w-full px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  Title
                </th>
                <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  Category
                </th>
                <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  Completeness
                </th>
                <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  Fields
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {displayItems.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-sm text-[var(--text-ter)]"
                  >
                    No items match.
                  </td>
                </tr>
              )}

              {displayItems.map((item) => {
                const score = scores[item.id];
                const pct = score?.pct ?? 0;
                const isOpen = item.id === selectedItemId;
                const category = (item as any).itemCategory as string | undefined;
                const badgeCls = category
                  ? (CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK)
                  : CATEGORY_BADGE_FALLBACK;
                const pctColor =
                  pct === 100
                    ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                    : pct >= 70
                    ? 'text-[var(--text-ter)]'
                    : 'text-[var(--text-ter)]';

                return (
                  <tr
                    key={item.id}
                    onClick={() =>
                      setSelectedItemId(isOpen ? null : item.id)
                    }
                    className={[
                      'cursor-pointer border-b border-[var(--rule)]/60 transition-colors',
                      isOpen
                        ? 'border-l-2 border-l-indigo-500 bg-[var(--slate-dim)]'
                        : 'border-l-2 border-l-transparent hover:bg-[var(--surface)]/60',
                    ].join(' ')}
                  >
                    {/* Title */}
                    <td className="px-4 py-3">
                      <p
                        className={`font-medium leading-snug ${
                          isOpen ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'
                        }`}
                      >
                        {item.title}
                      </p>
                    </td>

                    {/* Category */}
                    <td className="whitespace-nowrap px-4 py-3">
                      {category ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeCls}`}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--text-ter)]">—</span>
                      )}
                    </td>

                    {/* Segmented bar + % */}
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex min-w-[160px] items-center gap-3">
                        <div className="w-28 flex-shrink-0">
                          {score && (
                            <SegmentedBar
                              sections={score.sections}
                              total={score.total}
                            />
                          )}
                        </div>
                        <span
                          className={`w-8 text-right text-xs font-semibold ${pctColor}`}
                        >
                          {pct}%
                        </span>
                      </div>
                    </td>

                    {/* Fields count */}
                    <td className="whitespace-nowrap px-4 py-3">
                      {score && (
                        <span
                          className={`text-xs ${
                            pct === 100
                              ? 'font-medium text-[var(--green)] dark:text-[var(--text-ter)]'
                              : 'text-[var(--text-ter)]'
                          }`}
                        >
                          {score.complete} / {score.total}
                        </span>
                      )}
                    </td>

                    {/* External link */}
                    <td
                      className="px-2 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link
                        to={`/prioritization/items/${item.id}`}
                        className="flex items-center justify-center rounded p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-ter)] transition-all"
                        title="Open item"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Right slide-out panel */}
        <div
          className={`flex-shrink-0 overflow-hidden border-l border-[var(--rule)] transition-all duration-200 ${
            selectedItem ? 'w-[400px]' : 'w-0'
          }`}
        >
          {selectedItem && scores[selectedItem.id] && (
            <CompletenessPanel
              item={selectedItem}
              score={scores[selectedItem.id]}
              onClose={() => setSelectedItemId(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
